import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { Button } from 'src/components/Button';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { ConfirmModal } from 'src/components/ConfirmModal';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  executeManualTransaction,
  formatManualOwnerName,
  getManualExecutor,
  getManualOwner,
  getManualOwnerId,
  isManualExecuted,
  type ManualExecutionStatus,
  type ManualTransaction,
  type ManualTransactionKind,
} from 'src/api/adminManualTransactions';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

import AttachmentImage from './AttachmentImage';
import {
  AttachmentGrid,
  DecisionRow,
  DescriptionBlock,
  ExecutedNote,
  ReasonInput,
} from './styled';

type ManualTransactionModalProps = {
  kind: ManualTransactionKind;
  transaction: ManualTransaction;
  onClose: () => void;
  onExecuted?: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const ManualTransactionModal: React.FC<ManualTransactionModalProps> = ({
  kind,
  transaction,
  onClose,
  onExecuted,
}) => {
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<ManualExecutionStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const owner = getManualOwner(transaction);
  const ownerId = getManualOwnerId(transaction);
  const ownerName = formatManualOwnerName(owner);
  const executed = isManualExecuted(transaction);
  const executor = getManualExecutor(transaction.execution);
  const attachments = transaction.attachments ?? [];
  const created = transaction.created_at ? convertDate(transaction.created_at) : null;
  const decidedAt = transaction.execution?.timestamp
    ? convertDate(transaction.execution.timestamp)
    : null;

  const isDeposit = kind === 'deposit';
  const amount = `${transaction.quantity ?? '—'} ${transaction.currency_code ?? ''}`.trim();

  const runDecision = async (status: ManualExecutionStatus) => {
    setSubmitting(true);
    try {
      await executeManualTransaction(kind, transaction.id, {
        status,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      dispatch(
        setAppAlert({
          message:
            status === 'SUCCESS'
              ? `Manual ${kind} approved.`
              : `Manual ${kind} rejected.`,
          isSuccess: true,
        }),
      );
      onExecuted?.();
      onClose();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? `Failed to execute the manual ${kind}`,
          isSuccess: false,
        }),
      );
    } finally {
      setSubmitting(false);
      setPending(null);
    }
  };

  return (
    <>
      <Modal isOpen onClose={onClose}>
        <Breadcrumb>
          Manual {isDeposit ? 'deposits' : 'withdrawals'}
          {transaction.id ? ` / ${shortenString(transaction.id)}` : ''}
        </Breadcrumb>

        <HeadlineRow>
          <TitleGroup>
            <Title>
              {amount}
              {transaction.status ? (
                <StatusBadge $status={String(transaction.status)}>
                  {String(transaction.status)}
                </StatusBadge>
              ) : null}
            </Title>
            {created ? (
              <MetaLine>
                Requested {created.date}
                {created.time ? ` · ${created.time}` : ''}
              </MetaLine>
            ) : null}
          </TitleGroup>
        </HeadlineRow>

        <Card>
          <CardTitle>User</CardTitle>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Name</DetailLabel>
              {ownerId ? (
                <DetailValue>
                  <UserLink
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`${ROUTE_USERS}/${ownerId}`);
                    }}
                  >
                    {ownerName !== '—' ? ownerName : shortenString(String(ownerId))}
                  </UserLink>
                </DetailValue>
              ) : (
                <DetailValue $muted={ownerName === '—'}>{ownerName}</DetailValue>
              )}
            </DetailItem>
            <DetailItem>
              <DetailLabel>Email</DetailLabel>
              <DetailValue $muted={!owner?.email}>{owner?.email || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>BxLend ID</DetailLabel>
              <DetailValue $muted={!owner?.bxlend_id}>{owner?.bxlend_id || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Account status</DetailLabel>
              <DetailValue $muted={!owner?.status}>{owner?.status || '—'}</DetailValue>
            </DetailItem>
          </DetailGrid>
        </Card>
        <SectionSpacer />

        <Card>
          <CardTitle>Request</CardTitle>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Transaction ID</DetailLabel>
              <CopyableValue value={transaction.id} label="Transaction ID" />
            </DetailItem>
            <DetailItem>
              <DetailLabel>Amount</DetailLabel>
              <DetailValue>{amount}</DetailValue>
            </DetailItem>
          </DetailGrid>
          <SectionSpacer />
          <DetailLabel>Description</DetailLabel>
          <DescriptionBlock>{transaction.description || '—'}</DescriptionBlock>
        </Card>
        <SectionSpacer />

        {attachments.length > 0 && (
          <>
            <Card>
              <CardTitle>Proof ({attachments.length})</CardTitle>
              <AttachmentGrid>
                {attachments.map((attachmentId, index) => (
                  <AttachmentImage
                    key={attachmentId}
                    transactionId={transaction.id}
                    attachmentId={attachmentId}
                    index={index}
                  />
                ))}
              </AttachmentGrid>
            </Card>
            <SectionSpacer />
          </>
        )}

        <Card>
          <CardTitle>Decision</CardTitle>
          {executed ? (
            <ExecutedNote>
              Already executed
              {decidedAt ? ` on ${decidedAt.date}${decidedAt.time ? ` · ${decidedAt.time}` : ''}` : ''}
              {executor ? ` by ${formatManualOwnerName(executor)}` : ''}.
              {transaction.execution?.reason ? ` Reason: ${transaction.execution.reason}` : ''}
            </ExecutedNote>
          ) : (
            <PermissionGuard
              permission={PERMISSIONS.TRANSACTION_EXECUTE}
              fallback={
                <ExecutedNote>
                  You do not have permission to execute manual transactions.
                </ExecutedNote>
              }
            >
              <ReasonInput
                placeholder="Reason (recorded with the decision; required in practice when rejecting)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <DecisionRow>
                <Button
                  text="Approve"
                  className="black"
                  disabled={submitting}
                  onClick={() => setPending('SUCCESS')}
                />
                <Button
                  text="Reject"
                  className="negative"
                  disabled={submitting}
                  onClick={() => setPending('FAILED')}
                />
              </DecisionRow>
            </PermissionGuard>
          )}
        </Card>
      </Modal>

      {pending && (
        <ConfirmModal
          title={pending === 'SUCCESS' ? `Approve manual ${kind}` : `Reject manual ${kind}`}
          message={
            pending === 'SUCCESS' ? (
              <>
                Approve <strong>{amount}</strong> for{' '}
                <strong>{ownerName}</strong>?{' '}
                {isDeposit
                  ? 'This credits their wallet and cannot be undone.'
                  : 'This releases the funds and cannot be undone.'}
              </>
            ) : (
              <>
                Reject <strong>{amount}</strong> for <strong>{ownerName}</strong>? This
                cannot be undone.
              </>
            )
          }
          confirmText={pending === 'SUCCESS' ? 'Approve' : 'Reject'}
          confirmClassName={pending === 'SUCCESS' ? 'black' : 'negative'}
          onClose={() => setPending(null)}
          onConfirm={() => runDecision(pending)}
        />
      )}
    </>
  );
};

export default ManualTransactionModal;
